import React, { useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Controller, FieldValues, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAlertContext } from "../../contexts/alert/AlertProvider";
import TextInput from "../../components/common/input/TextInput";
import { asyncPostData, uploadFile } from "../../utils/httpRequests";
import FileInput from "../../components/common/input/FileInput";
import { LuX, LuImage, LuVideo } from "react-icons/lu";
import { v4 } from "uuid";
import { generateLinkImage } from "../../utils/helpers";
import RadiosInput from "../../components/common/input/RadiosInput";

type hinhThucType = "image" | "video";
const hinhthucs: { label: string; value: hinhThucType }[] = [
  {
    label: "Thi hình ảnh",
    value: "image",
  },
  {
    label: "Thi video",
    value: "video",
  },
];

type FormFields = {
  ho_ten: string;
  sdt: string;
  leader: string;
  f1: string;
  link_fb: string;
  hinh_thuc: string;
};

const schema = yup.object<FieldValues>({
  ho_ten: yup.string().required("Vui lòng nhập họ tên"),
  sdt: yup
    .string()
    .required("Vui lòng nhập số điện thoại")
    .matches(/^[0-9]{10,11}$/, {
      message: "Số điện thoại không đúng định dạng",
    }),
  leader: yup.string().required("Vui lòng nhập tên leader trực tiếp"),
  f1: yup.string().required("Vui lòng nhập F1 công ty"),
  link_fb: yup.string().required("Vui lòng nhập link facebook cá nhân"),
  hinh_thuc: yup.string().required("Vui lòng chọn hình thức dự thi"),
});

const MAX_SIZE_MB = 300;

function MainPage() {
  const defaultData: FormFields = {
    ho_ten: "",
    sdt: "",
    leader: "",
    f1: "",
    link_fb: "",
    hinh_thuc: "",
  };
  const { showAlert } = useAlertContext();
  const [sucess, setSuccess] = useState<boolean>(false);
  const [image, setImage] = useState<File | undefined>();
  const [video, setVideo] = useState<File | undefined>();
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { isSubmitting, errors },
  } = useForm<FieldValues>({
    mode: "onSubmit",
    defaultValues: defaultData,
    resolver: yupResolver(schema),
  });

  const hinhThuc: hinhThucType = watch("hinh_thuc");

  const onImageChange = (selectedFiles: FileList | undefined) => {
    if (!selectedFiles) return;
    setVideo(undefined);
    const image = selectedFiles[0];
    if (!image) return;
    const fileSizeMB = image.size / (1024 * 1024);
    if (fileSizeMB > MAX_SIZE_MB) {
      showAlert({
        type: "warning",
        message: `Kích thước file vượt quá ${MAX_SIZE_MB}MB`,
      });
    } else {
      setImage(image);
    }
  };
  const deleteImage = () => {
    if (!image) return;
    setImage(undefined);
  };

  const onVideoChange = (selectedFiles: FileList | undefined) => {
    if (!selectedFiles) return;
    setImage(undefined);
    const video = selectedFiles[0];
    if (!video) return;
    const fileSizeMB = video.size / (1024 * 1024);
    if (fileSizeMB > MAX_SIZE_MB) {
      showAlert({
        type: "warning",
        message: `Kích thước file vượt quá ${MAX_SIZE_MB}MB`,
      });
    } else {
      setVideo(video);
    }
  };
  const deleteVideo = () => {
    if (!video) return;
    setVideo(undefined);
  };

  const generateDataToPost = (values: any) => {
    return {
      ...values,
      exfields: {
        image: null,
        video: null,
      },
    };
  };

  const handleReset = () => {
    reset(defaultData);
    setImage(undefined);
    setVideo(undefined);
  };

  const submit = async (values: FieldValues) => {
    if (hinhThuc === "image" && !image) {
      showAlert({ type: "warning", message: "Vui lòng chọn hình ảnh" });
      return;
    }
    if (hinhThuc === "video" && !video) {
      showAlert({ type: "warning", message: "Vui lòng chọn video" });
      return;
    }
    const dataPost: { [key: string]: any } = generateDataToPost(values);
    // save image
    if (image) {
      const formData = new FormData();
      formData.append("file", image);
      const resp = await uploadFile({
        formData,
        folder: "event_spa",
      });
      if (!resp || resp?.status !== 200) {
        showAlert({
          type: "error",
          message:
            resp?.data?.error || resp?.data?.message || "Lỗi khi tải ảnh lên",
        });
        return;
      }
      const { fileUrl, ...rest } = resp?.data;
      dataPost.exfields.image = {
        ...rest,
        fileurl: fileUrl,
        url: generateLinkImage(resp?.data?.fileUrl),
      };
    }
    // save video
    if (video) {
      const formData = new FormData();
      formData.append("file", video);
      const resp = await uploadFile({
        formData,
        folder: "event_spa",
      });
      if (!resp || resp?.status !== 200) {
        showAlert({
          type: "error",
          message:
            resp?.data?.error || resp?.data?.message || "Lỗi khi tải video lên",
        });
        return;
      }
      const { fileUrl, ...rest } = resp?.data;
      dataPost.exfields.video = {
        ...rest,
        fileurl: fileUrl,
        url: generateLinkImage(resp?.data?.fileUrl),
      };
    }
    const resp = await asyncPostData({
      apiCode: "gameshow3",
      data: dataPost,
    });
    if (!resp || resp?.status !== 200) {
      showAlert({
        type: "error",
        message: resp?.data?.error || resp?.data?.message || "Có lỗi xảy ra",
      });
      return;
    }
    showAlert({
      type: "success",
      message: "Lưu thành công",
    });
    setSuccess(true);
    handleReset();
  };

  return (
    <Box
      sx={{
        py: 2,
        overflow: "auto",
        backgroundColor: "background.default",
        minHeight: "100vh",
      }}
    >
      <Container maxWidth="sm">
        <Box
          sx={{
            px: 2,
            py: 1,
            borderRadius: "10px",
            backgroundColor: "background.paper",
            width: "475px",
            maxWidth: "90vw",
            position: "relative",
            zIndex: 3,
          }}
        >
          <Stack gap={3}>
            <Stack alignItems="center">
              <Avatar
                src="/logo.svg"
                alt="logo"
                sx={{ width: "80px", height: "80px", borderRadius: "0px" }}
              />
              <Typography sx={{ fontWeight: 600, fontSize: "18px" }}>
                GOODCHARME
              </Typography>
            </Stack>
            <Stack gap={1}>
              <Typography
                sx={{
                  textAlign: "center",
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "primary.main",
                }}
              >
                MISS VERY SEXY - Quyến Rũ Đón Xuân Về
              </Typography>
            </Stack>
            {sucess && (
              <Alert severity="success">Lưu thông tin thành công</Alert>
            )}
            <Stack component="form" gap={2} onSubmit={handleSubmit(submit)}>
              <TextInput
                required
                multiline={false}
                direction="column"
                label="Họ tên"
                placeholder="Nhập họ tên"
                name="ho_ten"
                register={register}
                errorMessage={errors?.ho_ten?.message as string}
              />
              <Controller
                name="sdt"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <TextInput
                      required
                      value={value}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        // Kiểm tra nếu input chỉ chứa số
                        if (/^[0-9]*$/.test(inputValue)) {
                          onChange(inputValue);
                        }
                      }}
                      direction="column"
                      label="Số điện thoại"
                      placeholder="Nhập số điện thoại"
                      multiline={false}
                      inputMode="numeric"
                      errorMessage={errors?.sdt?.message as string}
                    />
                  );
                }}
              />
              <TextInput
                required
                direction="column"
                label="Tên Leader trực tiếp"
                placeholder="Nhập tên leader trực tiếp"
                name="leader"
                multiline={false}
                register={register}
                errorMessage={errors?.leader?.message as string}
              />
              <TextInput
                required
                direction="column"
                label="Trực thuộc team F1"
                placeholder="Nhập trực thuộc team F1 công ty"
                name="f1"
                multiline={false}
                register={register}
                errorMessage={errors?.f1?.message as string}
              />
              <TextInput
                required
                direction="column"
                label="Link Facebook"
                placeholder="Nhập link facebook cá nhân"
                name="link_fb"
                register={register}
                errorMessage={errors?.link_fb?.message as string}
              />
              <Box>
                <Controller
                  name="hinh_thuc"
                  control={control}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <RadiosInput
                        row={false}
                        value={value}
                        onChange={onChange}
                        label="Hình thức dự thi"
                        direction="column"
                        options={hinhthucs}
                        groupSx={{ justifyContent: "space-between" }}
                        errorMessage={errors?.hinh_thuc?.message as string}
                      />
                    );
                  }}
                />
              </Box>
              {!!hinhThuc && (
                <>
                  {hinhThuc === "image" ? (
                    <Stack
                      gap={1}
                      sx={{
                        p: 1,
                        borderRadius: "10px",
                        border: "1px dashed",
                        borderColor: "divider",
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography>Hình ảnh</Typography>
                        <FileInput
                          key={v4()}
                          accept="image/*"
                          onChange={onImageChange}
                          renderDisplay={({ openSelectFile }) => {
                            return (
                              <Button
                                onClick={openSelectFile}
                                variant="contained"
                                color="secondary"
                                startIcon={<LuImage size={16} />}
                                sx={{
                                  maxWidth: "160px",
                                  boxShadow: "none",
                                  textTransform: "none",
                                  "&:hover": { boxShadow: "none" },
                                }}
                              >
                                Chọn ảnh
                              </Button>
                            );
                          }}
                        />
                      </Stack>
                      {!!image && (
                        <Stack
                          direction="row"
                          alignItems="center"
                          gap={1}
                          justifyContent="space-between"
                        >
                          <Typography
                            sx={{ fontWeight: 500, lineBreak: "anywhere" }}
                          >
                            {image.name}
                          </Typography>
                          <IconButton onClick={deleteImage}>
                            <LuX size={20} />
                          </IconButton>
                        </Stack>
                      )}
                    </Stack>
                  ) : (
                    <Stack
                      gap={1}
                      sx={{
                        p: 1,
                        borderRadius: "10px",
                        border: "1px dashed",
                        borderColor: "divider",
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography>Video</Typography>
                        <FileInput
                          key={v4()}
                          accept="video/*"
                          onChange={onVideoChange}
                          renderDisplay={({ openSelectFile }) => {
                            return (
                              <Button
                                onClick={openSelectFile}
                                variant="contained"
                                color="secondary"
                                startIcon={<LuVideo size={16} />}
                                sx={{
                                  maxWidth: "160px",
                                  boxShadow: "none",
                                  textTransform: "none",
                                  "&:hover": { boxShadow: "none" },
                                }}
                              >
                                Chọn video
                              </Button>
                            );
                          }}
                        />
                      </Stack>
                      {!!video && (
                        <Stack
                          direction="row"
                          alignItems="center"
                          gap={1}
                          justifyContent="space-between"
                        >
                          <Typography
                            sx={{ fontWeight: 500, lineBreak: "anywhere" }}
                          >
                            {video.name}
                          </Typography>
                          <IconButton onClick={deleteVideo}>
                            <LuX size={20} />
                          </IconButton>
                        </Stack>
                      )}
                    </Stack>
                  )}
                </>
              )}
              <LoadingButton
                type="submit"
                loading={isSubmitting}
                variant="contained"
                sx={{
                  height: "45px",
                  boxShadow: "none",
                  color: "common.white",
                  "&:hover": {
                    backgroundColor: "primary.main",
                    boxShadow: "none",
                  },
                }}
              >
                Lưu lại
              </LoadingButton>
            </Stack>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}

export default MainPage;
